import { addDays, addHours, addMilliseconds, format, isValid, parseISO, parseJSON } from 'date-fns';
import { formatInTimeZone, getTimezoneOffset } from 'date-fns-tz';

import { DateRange, DefinedRange } from '@topconagriculture/react-components';
import { DateLike } from '@topconagriculture/language';

import { DateRangeType, PresetDateRanges } from '../../../config/constants';
import {
  lastMonthDateRange,
  last7DaysDateRange,
  todayDateRange,
  yesterdayDateRange,
} from '../../../shared/DateRangePicker/utils';

/**
 * Returns DefinedRange object with values calculated based on current dates
 */
export const getPresetDateRange = (rangeKey: string): DefinedRange | undefined => {
  const dateNow = new Date();
  const ranges = {
    [PresetDateRanges.TODAY]: () => todayDateRange(dateNow),
    [PresetDateRanges.YESTERDAY]: () => yesterdayDateRange(dateNow),
    [PresetDateRanges.LAST_7_DAYS]: () => last7DaysDateRange(dateNow),
    [PresetDateRanges.LAST_MONTH]: () => lastMonthDateRange(dateNow),
  };
  return ranges[rangeKey] ? ranges[rangeKey]() : undefined;
};

export const getStartEndDates = (
  givenEndDate?: Date,
  daysBack?: number,
  dateRangeType?: string | DateRangeType,
): DateRange => {
  const dateNow = new Date();

  switch (dateRangeType) {
    case DateRangeType.LAST_MONTH:
      return lastMonthDateRange(dateNow);
    case DateRangeType.YESTERDAY:
      return yesterdayDateRange(dateNow);
    default:
      const endDate = givenEndDate ? new Date(givenEndDate) : undefined;
      const startDate = endDate ? addDays(endDate, -1 * (daysBack || 0)) : undefined;
      return {
        startDate,
        endDate,
      };
  }
};

export const toUTCDate = (date: Date): Date => {
  const offset = date.getTimezoneOffset() / 60;
  return addHours(date, offset);
};

export const toLocalDate = (date: Date): Date => {
  const offset = date.getTimezoneOffset() / 60;
  return addHours(date, -offset);
};

export const toTimezoneDate = (date: Date, timezone: string) => {
  const offset = getTimezoneOffset(timezone, date);
  return addMilliseconds(date, -offset);
};

export const toSelectedTimezoneDate = (date: Date | string, timezone: string, dateFormat: string) => {
  const localeDate = toLocalDate(new Date(date));
  const formatedDate = formatInTimeZone(localeDate, timezone, dateFormat);
  return formatedDate;
};

export function parseDate(date: DateLike): Date | null {
  if (date === undefined || date === null) {
    return null;
  }
  let parsedDate: Date;
  switch (typeof date) {
    case 'string':
      parsedDate = parseISO(date);
      break;
    case 'number':
    case 'object': // Date obj
      parsedDate = parseJSON(date);
      break;
  }

  if (!isValid(parsedDate)) {
    return null;
  }
  return parsedDate;
}

export function formatDate(date: DateLike, formatToken: string, options?: Parameters<typeof format>[2]): string {
  const parsedDate = parseDate(date);
  if (!parsedDate) {
    return '';
  }
  return format(parsedDate, formatToken, options);
}

export const toFarmTimeZone = (date: Date, timezone: string): Date => {
  const offset = getTimezoneOffset(timezone, date);
  return addMilliseconds(date, offset);
};
